// ABOUTME: Second step in external channel creation - collects channel name and API credentials
// ABOUTME: Includes test connection functionality to validate credentials before proceeding

import { useState } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import * as yup from "yup";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { yupRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useDispatch, useSelector } from 'react-redux';
import Shbutton from '../../../../theme/buttons/Shbutton';
import { useTestExternalChannelConnectionMutation } from '../../../../../newapi/externalchannel/externalChannelSlice';
import ShLabel from '../../../../theme/label/ShLabel';
import { useGetSubscriptionPricesQuery } from '../../../../../newapi/subscription/subscriptionSlice';
import PredikoSubscriptionInfo from '../components/PredikoSubscriptionInfo';

// Outlook connects its mailbox with OAuth from the channel sidepanel, Ampere and SMTP/IMAP
// collect their credentials on the configuration step. None of them holds an api key.
const CHANNELS_WITHOUT_API_KEY = ['outlook', 'ampere', 'smtpimap'];

const Content = (props) => {
    const { t } = useTranslation();
    const data = { ...props.data };
    const dispatch = useDispatch();
    const [testConnection, { isLoading: isTestingConnection }] = useTestExternalChannelConnectionMutation();
    const [connectionStatus, setConnectionStatus] = useState(null); // null, 'success', 'error'
    const usesApiKey = !CHANNELS_WITHOUT_API_KEY.includes(data.channel_type);

    const handleChange = (key, val) => {
        if (key === 'apiKey') {
            // Store apiKey in credentials object
            props.handleDataUpdate({
                credentials: {
                    ...(data.credentials || {}),
                    apiKey: val
                }
            });
            setConnectionStatus(null);
        } else {
            props.handleDataUpdate({ [key]: val });
        }
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const handleTestConnection = async () => {
        if (!data.credentials?.apiKey) {
            return;
        }

        try {
            const result = await testConnection({
                id: 'test',
                credentials: { apiKey: data.credentials.apiKey, type: data.channel_type }
            }).unwrap();

            setConnectionStatus('success');
        } catch (error) {
            setConnectionStatus('error');
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: 1,
                    paddingTop: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 7
                }}
            >
                <Box sx={{ width: 120 }}>
                    <Text>{t("channel_name")}</Text>
                </Box>

                <ShValidatedInput
                    name="description"
                    value={data.description ?? ""}
                    onChange={handleChange}
                    error={props.errors.description}
                />
            </Box>

            {usesApiKey && <Box
                sx={{
                    width: 1,
                    paddingTop: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 7
                }}
            >
                <Box sx={{ width: 120 }}>
                    <Text>{t("api_key")}</Text>
                </Box>

                <ShValidatedInput
                    name="apiKey"
                    type="password"
                    value={data.credentials?.apiKey ?? ""}
                    onChange={handleChange}
                    error={props.errors.apiKey}
                />
            </Box>}

            {usesApiKey && <Box
                sx={{
                    width: 1,
                    paddingTop: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 2
                }}
            >
                <Shbutton
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={!data.credentials?.apiKey || isTestingConnection}
                    sx={{ minWidth: 150 }}
                >
                    {t('test_connection')}
                </Shbutton>

                {isTestingConnection ? (
                    <ShLabel variant="blue" palette="normal" fitted>{t('connecting')}</ShLabel>
                ) : connectionStatus === 'error' ? (
                    <ShLabel variant="red" palette="normal" fitted>{t('connection_failed')}</ShLabel>
                ) : connectionStatus === 'success' ? (
                    <ShLabel variant="green" palette="normal" fitted>{t('connected')}</ShLabel>
                ) : null}
            </Box>}

            {data.channel_type === 'outlook' && (
                <Box sx={{ width: 1, paddingTop: 2 }}>
                    <Text light>{t("outlook_connect_after_save")}</Text>
                </Box>
            )}

            {/* Informational subscription price for prediko (subscription is managed by the backend) */}
            {data.channel_type === 'prediko' && (
                <PredikoSubscriptionInfo />
            )}
        </>
    );
};

const ChannelBasicInfo = (props) => {
    const { t } = useTranslation();
    // Block proceeding past this step for prediko until the fulfilment has set the
    // monthly subscription price for the relation's invoicecategory. The backend
    // enforces this too; this just gives immediate UX feedback.
    const channelType = useSelector((state) => state.progressiontabs.data?.channel_type);
    const isPrediko = channelType === 'prediko';
    const { data: predikoPrices } = useGetSubscriptionPricesQuery('prediko', { skip: !isPrediko });
    const predikoPricesConfigured = !!predikoPrices
        && predikoPrices.monthly !== null && predikoPrices.monthly !== undefined;

    const schema = CHANNELS_WITHOUT_API_KEY.includes(channelType)
        ? yup.object({ description: yupRequired('channel_name', t) })
        : yup.object({
            description: yupRequired('channel_name', t),
            credentials: yup.object({
                apiKey: yupRequired('api_key', t),
            }).required()
        });

    return (
        <ProgressionTab
            tabId="tabBasicInfo"
            subtitle={t("authorisation")}
            step={2}
            nextTab={"tabConfiguration"}
            maxSteps={3}
            schema={schema}
            nextDisabled={isPrediko && !predikoPricesConfigured}
        >
            <Content />
        </ProgressionTab>
    )
}

export default ChannelBasicInfo;
